import React, { useMemo } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import {
  gridCellToRollupNumber,
  inputTypeFromDataType,
  parseGridKpiValue,
  serializeGridKpiValue,
  type GridKpiRow,
} from "@core/reports/utils/fieldTypeUtils";

export type KpiFieldGridColumn = {
  column_key?: string | null;
  label: string;
  data_type_name?: string | null;
  data_type_id?: number;
  list_order?: number;
  include_in_rollup?: boolean;
  is_active?: boolean;
  options?: Array<{
    option_id?: number;
    kpi_option_id?: number;
    label: string;
    list_order?: number;
    allows_input?: boolean;
    is_active?: boolean;
  }>;
};

export type KpiFieldGridFixedRow = {
  kpi_grid_fixed_row_id?: number;
  row_key?: string | null;
  label: string;
  list_order?: number;
  is_active?: boolean;
};

export interface KpiGridEditorProps {
  inputId: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  gridColumns?: KpiFieldGridColumn[] | null;
  gridFixedRows?: KpiFieldGridFixedRow[] | null;
  gridMinRows?: number | null;
  gridMaxRows?: number | null;
  gridShowColumnTotals?: boolean | null;
  gridShowRowTotals?: boolean | null;
}

/** Matches API KpiValueLimits::GRID_FIXED_ROWS_LAST — free block then fixed. */
const GRID_FIXED_ROWS_LAST = true;

function emptyFreeRow(columns: KpiFieldGridColumn[]): GridKpiRow {
  const row: GridKpiRow = { row_kind: "free" };
  for (const col of columns) {
    const key = col.column_key || "";
    if (key) row[key] = "";
  }
  return row;
}

function fixedRowFromDef(
  def: KpiFieldGridFixedRow,
  columns: KpiFieldGridColumn[],
  existing?: GridKpiRow
): GridKpiRow {
  const firstKey = columns[0]?.column_key || "";
  const row: GridKpiRow = { row_kind: "fixed" };
  if (def.row_key) row.row_key = def.row_key;
  for (const col of columns) {
    const key = col.column_key || "";
    if (!key) continue;
    if (key === firstKey) {
      row[key] = def.label;
    } else {
      row[key] = existing?.[key] ?? "";
    }
  }
  return row;
}

/**
 * Merge stored value with live/snapshot fixed-row defs.
 * Free rows first when GRID_FIXED_ROWS_LAST; hydrate missing active fixed rows.
 */
function coerceDisplayRows(
  parsedRows: GridKpiRow[],
  columns: KpiFieldGridColumn[],
  fixedDefs: KpiFieldGridFixedRow[],
  minFreeRows: number
): GridKpiRow[] {
  const free: GridKpiRow[] = [];
  const fixedByKey = new Map<string, GridKpiRow>();

  for (const row of parsedRows) {
    const kind = String(row.row_kind || "").toLowerCase();
    const key = row.row_key != null ? String(row.row_key) : "";
    if (kind === "fixed" || (key && fixedDefs.some((d) => d.row_key === key))) {
      if (key) fixedByKey.set(key, row);
      continue;
    }
    free.push({ ...row, row_kind: "free" });
  }

  // Legacy rows without row_kind: treat unmatched as free.
  while (free.length < minFreeRows) {
    free.push(emptyFreeRow(columns));
  }

  const fixed = fixedDefs.map((def) => {
    const existing = def.row_key ? fixedByKey.get(def.row_key) : undefined;
    return fixedRowFromDef(def, columns, existing);
  });

  return GRID_FIXED_ROWS_LAST ? [...free, ...fixed] : [...fixed, ...free];
}

function formatTotal(n: number | null): string {
  if (n == null) return "";
  return Number(n).toLocaleString();
}

/**
 * Controlled KPI GRID table editor (free + fixed rows, derived column/row totals).
 * Shared by the inline activity-entry field and the maximize modal.
 */
export default function KpiGridEditor({
  inputId,
  value,
  onChange,
  disabled,
  gridColumns,
  gridFixedRows,
  gridMinRows,
  gridMaxRows,
  gridShowColumnTotals,
  gridShowRowTotals,
}: KpiGridEditorProps) {
  const columns = useMemo(
    () =>
      (gridColumns ?? [])
        .filter((c) => c.is_active !== false)
        .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)),
    [gridColumns]
  );

  const fixedDefs = useMemo(
    () =>
      (gridFixedRows ?? [])
        .filter((r) => r.is_active !== false)
        .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)),
    [gridFixedRows]
  );

  const parsed = parseGridKpiValue(value);
  const minFree = gridMinRows != null && gridMinRows > 0 ? gridMinRows : 0;
  const maxFree =
    gridMaxRows != null && gridMaxRows > 0 ? gridMaxRows : undefined;

  const rows = useMemo(
    () => coerceDisplayRows(parsed.rows, columns, fixedDefs, minFree),
    // parsed.rows identity changes with value; depend on value string via parsed content
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, columns, fixedDefs, minFree]
  );

  const freeCount = rows.filter(
    (r) => String(r.row_kind || "").toLowerCase() !== "fixed"
  ).length;
  const canAdd = maxFree == null || freeCount < maxFree;
  const canRemoveFree = freeCount > minFree;

  const firstColKey = columns[0]?.column_key || "";
  const showColTotals = !!gridShowColumnTotals;
  const showRowTotals = !!gridShowRowTotals;

  const updateRows = (nextRows: GridKpiRow[]) => {
    onChange(
      serializeGridKpiValue(
        {
          rows: nextRows,
          column_labels: parsed.column_labels,
          column_order: parsed.column_order,
        },
        columns
      )
    );
  };

  const setCell = (rowIndex: number, columnKey: string, cellValue: string) => {
    const next = rows.map((r, i) =>
      i === rowIndex ? { ...r, [columnKey]: cellValue } : r
    );
    updateRows(next);
  };

  const rowTotalAt = (row: GridKpiRow): number | null => {
    let sum = 0;
    let any = false;
    for (const col of columns) {
      if (!col.include_in_rollup) continue;
      const key = col.column_key || "";
      if (!key) continue;
      const n = gridCellToRollupNumber(row[key], col.data_type_name);
      if (n != null) {
        sum += n;
        any = true;
      }
    }
    return any ? sum : null;
  };

  const columnTotals = columns.map((col) => {
    if (!col.include_in_rollup) return null;
    const key = col.column_key || "";
    if (!key) return null;
    let sum = 0;
    let any = false;
    for (const row of rows) {
      const n = gridCellToRollupNumber(row[key], col.data_type_name);
      if (n != null) {
        sum += n;
        any = true;
      }
    }
    return any ? sum : null;
  });

  const cornerTotal = (() => {
    if (!showColTotals || !showRowTotals) return null;
    let sum = 0;
    let any = false;
    for (const row of rows) {
      const n = rowTotalAt(row);
      if (n != null) {
        sum += n;
        any = true;
      }
    }
    return any ? sum : null;
  })();

  // Keep columns readable: scroll horizontally instead of crushing inputs.
  let tableMinWidthRem = columns.length * 11;
  if (showRowTotals) tableMinWidthRem += 8;
  if (!disabled) tableMinWidthRem += 3.5;
  tableMinWidthRem = Math.max(40, tableMinWidthRem);

  return (
    <div className="table-responsive" data-testid={`${inputId}-grid`}>
      <Table
        bordered
        className="mb-2 align-middle kpi-field-grid"
        style={{ minWidth: `${tableMinWidthRem}rem` }}
      >
        <thead className="table-light">
          <tr>
            {columns.map((col) => (
              <th key={col.column_key || col.label}>{col.label}</th>
            ))}
            {showRowTotals ? <th>Row total</th> : null}
            {!disabled ? (
              <th className="kpi-field-grid__actions" style={{ width: 48 }} />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const isFixed = String(row.row_kind || "").toLowerCase() === "fixed";
            return (
              <tr
                key={
                  isFixed && row.row_key
                    ? `fixed-${row.row_key}`
                    : `row-${rowIndex}`
                }
              >
                {columns.map((col) => {
                  const key = col.column_key || "";
                  const dt = col.data_type_name || "";
                  const cellType = inputTypeFromDataType(dt);
                  const isHeadingCell = !!key && key === firstColKey && isFixed;
                  return (
                    <td key={key || col.label}>
                      {isHeadingCell ? (
                        <Form.Control
                          type="text"
                          value={String(row[key] ?? "")}
                          disabled
                          readOnly
                          className="bg-light"
                        />
                      ) : (
                        <Form.Control
                          type={cellType}
                          inputMode={cellType === "number" ? "decimal" : undefined}
                          step={cellType === "number" ? "any" : undefined}
                          value={key ? String(row[key] ?? "") : ""}
                          disabled={disabled || !key}
                          onChange={(e) => {
                            if (!key) return;
                            setCell(rowIndex, key, e.target.value);
                          }}
                        />
                      )}
                    </td>
                  );
                })}
                {showRowTotals ? (
                  <td className="table-light fw-semibold text-end">
                    {formatTotal(rowTotalAt(row))}
                  </td>
                ) : null}
                {!disabled ? (
                  <td className="kpi-field-grid__actions">
                    {isFixed ? null : (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ntcog-table-action-btn ntcog-table-action-btn--danger"
                        disabled={!canRemoveFree}
                        onClick={() => {
                          updateRows(rows.filter((_, i) => i !== rowIndex));
                        }}
                        aria-label={`Remove row ${rowIndex + 1}`}
                      >
                        <FiTrash2 />
                      </Button>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
          {showColTotals ? (
            <tr className="table-light fw-semibold">
              {columns.map((col, i) => (
                <td key={col.column_key || col.label} className="text-end">
                  {i === 0
                    ? columnTotals[i] == null
                      ? "Total"
                      : formatTotal(columnTotals[i])
                    : formatTotal(columnTotals[i])}
                </td>
              ))}
              {showRowTotals ? (
                <td className="text-end">{formatTotal(cornerTotal)}</td>
              ) : null}
              {!disabled ? <td className="kpi-field-grid__actions" /> : null}
            </tr>
          ) : null}
        </tbody>
      </Table>
      {!disabled ? (
        <Button
          variant="outline-primary"
          size="sm"
          className="btn-action"
          disabled={!canAdd}
          onClick={() => {
            const free = rows.filter(
              (r) => String(r.row_kind || "").toLowerCase() !== "fixed"
            );
            const fixed = rows.filter(
              (r) => String(r.row_kind || "").toLowerCase() === "fixed"
            );
            const nextFree = [...free, emptyFreeRow(columns)];
            updateRows(
              GRID_FIXED_ROWS_LAST ? [...nextFree, ...fixed] : [...fixed, ...nextFree]
            );
          }}
        >
          <FiPlus className="me-1" />
          Add row
        </Button>
      ) : null}
    </div>
  );
}
